import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { Box, Typography } from '@mui/material';

import { InlineNotification } from '@paragon/notification-tools';

import { RootState } from '../../../../store';
import { getActiveComponentDesignGuid } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { useAppSelector } from '../../../hooks';
import { CostComparisonCard } from './CostComparisonCard';
import { formatOptimizationCost } from './formatOptimizationCost';
import {
  annotateOptimizedBreakdownForComparison,
  calculateOptimizationSavings,
  GroupedOptimizationCostBreakdown,
  mapBreakdownByTermToViewModel,
} from './optimizationCostBreakdown';

interface CostComparisonForDisplay {
  readonly previousDesign: GroupedOptimizationCostBreakdown;
  readonly optimizedDesign: GroupedOptimizationCostBreakdown;
  readonly savings: number;
  readonly lumberOnly: boolean;
}

const getOptimizationCostComparisonForDisplay = createSelector(
  (state: RootState) => state.optimizationCostComparison.latestComparison,
  getActiveComponentDesignGuid,
  (latestComparison, activeComponentDesignGuid): CostComparisonForDisplay | null => {
    if (latestComparison == null || latestComparison.componentDesignGuid !== activeComponentDesignGuid) {
      return null;
    }
    const previousDesign = mapBreakdownByTermToViewModel(latestComparison.comparison.previousDesign);
    const optimizedDesign = annotateOptimizedBreakdownForComparison(
      mapBreakdownByTermToViewModel(latestComparison.comparison.optimizedDesign),
      previousDesign,
    );
    return {
      previousDesign,
      optimizedDesign,
      savings: calculateOptimizationSavings(previousDesign.total, optimizedDesign.total),
      lumberOnly: latestComparison.comparison.lumberOnly,
    };
  },
);

export function CostComparisonSection() {
  const comparison = useAppSelector(getOptimizationCostComparisonForDisplay);

  if (comparison == null) {
    return (
      <InlineNotification type="info">
        A before/after cost comparison will appear here once an optimization run reports per-term costs.
      </InlineNotification>
    );
  }

  const savingsCents = Math.round(comparison.savings * 100);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {comparison.lumberOnly && (
        <InlineNotification type="info">
          Lumber-only optimization: plate costs are excluded from this comparison.
        </InlineNotification>
      )}
      <CostComparisonCard title="Previous design" breakdown={comparison.previousDesign} />
      <CostComparisonCard title="Optimized design" breakdown={comparison.optimizedDesign} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1 }}>
        <Typography variant="body2">Savings</Typography>
        <Typography
          variant="body2"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            color: savingsCents > 0 ? 'success.main' : savingsCents < 0 ? 'warning.main' : 'text.primary',
          }}
        >
          {formatOptimizationCost(comparison.savings)}
        </Typography>
      </Box>
    </Box>
  );
}
